/**
 * useSongPreview — shared "listen to the song" logic for the open-map dialogs.
 *
 * Owns a single <audio> element (created lazily, no JSX tag needed) so only one
 * preview plays at a time across the whole list. `toggle(id, url)` starts the
 * given track (or stops it if it's already the one playing); `stop()` halts
 * playback (call it when the dialog closes).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useSongPreview() {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const a = new Audio();
    a.onended = () => setPreviewId(null);
    audioRef.current = a;
    return () => { a.pause(); a.onended = null; audioRef.current = null; };
  }, []);

  const toggle = useCallback((id: string, url: string) => {
    const a = audioRef.current;
    if (!a) return;
    if (previewId === id) { a.pause(); setPreviewId(null); return; }
    a.src = url;
    a.currentTime = 0;
    void a.play().catch(() => { /* autoplay/network — ignore */ });
    setPreviewId(id);
  }, [previewId]);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    setPreviewId(null);
  }, []);

  return { previewId, toggle, stop };
}
